import React, { useState, useEffect, useCallback } from 'react';
import { getLeads, getLeadsGrouped, getManagers, getLeadStages, createLead, updateLead, deleteLead } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  Plus,
  Calendar as CalendarIcon,
  Search,
  Users,
  Edit,
  Trash2,
  Loader2,
  DollarSign,
  UserCircle,
  Eye,
  UsersRound,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const getStageStyle = (color) => {
  const styles = {
    blue: 'badge-blue',
    yellow: 'badge-yellow',
    green: 'badge-green',
    red: 'badge-red',
    gray: 'badge-gray',
  };
  return styles[color] || 'badge-gray';
};

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [groupedLeads, setGroupedLeads] = useState([]);
  const [managers, setManagers] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const { user, isAdmin, isTeamLeader } = useAuth();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [viewingLead, setViewingLead] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    stage_id: '',
    amount: '',
    manager_id: '',
    visit_date: null,
    last_payment_date: null,
    comment: '',
  });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [managersData, stagesData] = await Promise.all([
        getManagers(),
        getLeadStages(),
      ]);
      setManagers(managersData);
      setStages(stagesData);

      // Admin/Team Leader sees grouped leads, manager sees flat list
      if (isAdmin || isTeamLeader) {
        const grouped = await getLeadsGrouped();
        setGroupedLeads(grouped);
      } else {
        const leadsData = await getLeads(stageFilter !== 'all' ? stageFilter : null);
        setLeads(leadsData);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [stageFilter, isAdmin, isTeamLeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get all leads for admin/team leader (for filtering)
  const allLeads = (isAdmin || isTeamLeader)
    ? groupedLeads.flatMap(g => (g.managers || []).flatMap(m => m.leads.map(l => ({ ...l, manager_id: m.manager_id, manager_name: m.manager_name }))))
    : leads;

  const filteredLeads = allLeads.filter((lead) => {
    const matchesSearch = lead.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStage = stageFilter === 'all' || lead.stage_id === stageFilter;
    return matchesSearch && matchesStage;
  });

  const openCreateDialog = () => {
    setEditingLead(null);
    setFormData({
      name: '',
      stage_id: stages[0]?.id || '',
      amount: '',
      manager_id: user?.manager_id || (managers[0]?.id || ''),
      visit_date: null,
      last_payment_date: null,
      comment: '',
    });
    setDialogOpen(true);
  };

  const openViewDialog = (lead, managerName = null) => {
    setViewingLead({ ...lead, manager_name_display: managerName });
    setViewDialogOpen(true);
  };

  const openEditDialog = (lead, managerId = null) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name,
      stage_id: lead.stage_id,
      amount: lead.amount?.toString() || '',
      manager_id: managerId || lead.manager_id,
      visit_date: lead.visit_date ? parseISO(lead.visit_date) : null,
      last_payment_date: lead.last_payment_date ? parseISO(lead.last_payment_date) : null,
      comment: lead.comment || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.manager_id || !formData.stage_id) return;

    try {
      setSaving(true);
      const data = {
        name: formData.name,
        stage_id: formData.stage_id,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        manager_id: formData.manager_id,
        visit_date: formData.visit_date ? format(formData.visit_date, 'yyyy-MM-dd') : null,
        last_payment_date: formData.last_payment_date
          ? format(formData.last_payment_date, 'yyyy-MM-dd')
          : null,
        comment: formData.comment || null,
      };

      if (editingLead) {
        await updateLead(editingLead.id, data);
        toast.success('Лид обновлён');
      } else {
        await createLead(data);
        toast.success('Лид создан');
      }

      setDialogOpen(false);
      fetchData();
    } catch (err) {
      console.error('Failed to save lead:', err);
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Удалить лид?')) return;

    try {
      await deleteLead(leadId);
      toast.success('Лид удалён');
      fetchData();
    } catch (err) {
      console.error('Failed to delete lead:', err);
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const handleQuickStageChange = async (lead, newStageId, managerId = null) => {
    try {
      await updateLead(lead.id, {
        name: lead.name,
        stage_id: newStageId,
        amount: lead.amount,
        manager_id: managerId || lead.manager_id,
        visit_date: lead.visit_date,
        last_payment_date: lead.last_payment_date,
        comment: lead.comment,
      });
      toast.success('Этап изменён');
      fetchData();
    } catch (err) {
      console.error('Failed to update stage:', err);
      toast.error('Ошибка изменения этапа');
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '—';
    return new Intl.NumberFormat('ru-RU', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value) + ' $';
  };

  const LeadCard = ({ lead, managerId = null, managerName = null }) => (
    <Card className="animate-fade-in" data-testid={`lead-${lead.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="font-medium">{lead.name}</h3>
              <span
                className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                  getStageStyle(lead.stage_color)
                )}
              >
                {lead.stage_name}
              </span>
            </div>

            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              {lead.amount && (
                <div className="flex items-center gap-1">
                  <DollarSign className="h-4 w-4" />
                  <span>{formatCurrency(lead.amount)}</span>
                </div>
              )}
              {lead.visit_date && (
                <div className="flex items-center gap-1">
                  <CalendarIcon className="h-4 w-4" />
                  <span>
                    Визит: {format(parseISO(lead.visit_date), 'd MMM', { locale: ru })}
                  </span>
                </div>
              )}
            </div>

            {lead.comment && (
              <p className="text-sm mt-2 text-muted-foreground">{lead.comment}</p>
            )}
          </div>

          <div className="flex gap-1 ml-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => openViewDialog(lead, managerName)}
              data-testid={`view-lead-${lead.id}`}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => openEditDialog(lead, managerId)}
              data-testid={`edit-lead-${lead.id}`}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => handleDelete(lead.id)}
              data-testid={`delete-lead-${lead.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Лиды</h1>
          <p className="text-muted-foreground">Управление клиентами</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-lead-btn">
          <Plus className="h-4 w-4 mr-2" />
          Создать
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск по имени..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-leads-input"
          />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="stage-filter">
            <SelectValue placeholder="Этап" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все этапы</SelectItem>
            {stages.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Leads List */}
      {isAdmin || isTeamLeader ? (
        // Admin/Team Leader view - grouped by team and manager
        groupedLeads.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Нет лидов</h3>
              <p className="text-muted-foreground mb-4">Создайте первого лида</p>
              {isAdmin && (
                <Button onClick={openCreateDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Создать лид
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {groupedLeads.map((teamGroup) => (
              <div key={teamGroup.team_id || 'no-team'} className="space-y-4">
                {/* Team header */}
                <div className="flex items-center gap-3 pb-2 border-b">
                  <UsersRound className="h-5 w-5 text-primary" />
                  <h2 className="text-lg font-semibold">{teamGroup.team_name}</h2>
                </div>
                
                {/* Managers in team */}
                <Accordion type="multiple" defaultValue={teamGroup.managers?.map(m => m.manager_id) || []} className="space-y-3">
                  {(teamGroup.managers || []).map((group) => {
                    // Filter leads in this group
                    const groupFilteredLeads = group.leads.filter((lead) => {
                      const matchesSearch = lead.name.toLowerCase().includes(searchQuery.toLowerCase());
                      const matchesStage = stageFilter === 'all' || lead.stage_id === stageFilter;
                      return matchesSearch && matchesStage;
                    });

                    if (groupFilteredLeads.length === 0 && (searchQuery || stageFilter !== 'all')) {
                      return null;
                    }

                    return (
                      <AccordionItem key={group.manager_id} value={group.manager_id} className="border rounded-lg">
                        <AccordionTrigger className="px-4 hover:no-underline">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <UserCircle className="h-4 w-4 text-primary" />
                            </div>
                            <span className="font-medium">{group.manager_name}</span>
                            <span className="text-sm text-muted-foreground">
                              ({groupFilteredLeads.length} {groupFilteredLeads.length === 1 ? 'лид' : 'лидов'})
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          {groupFilteredLeads.length === 0 ? (
                            <p className="text-muted-foreground text-center py-4">Нет лидов</p>
                          ) : (
                            <div className="space-y-3">
                              {groupFilteredLeads.map((lead) => (
                                <LeadCard key={lead.id} lead={lead} managerId={group.manager_id} managerName={group.manager_name} />
                              ))}
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>
            ))}
          </div>
        )
      ) : (
        // Manager view - flat list
        filteredLeads.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Нет лидов</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || stageFilter !== 'all'
                  ? 'Попробуйте изменить фильтры'
                  : 'Создайте первого лида'}
              </p>
              {!searchQuery && stageFilter === 'all' && (
                <Button onClick={openCreateDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Создать лид
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredLeads.map((lead) => (
              <LeadCard key={lead.id} lead={lead} />
            ))}
          </div>
        )
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLead ? 'Редактировать лид' : 'Создать лид'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Имя клиента *</Label>
              <Input
                placeholder="Введите имя"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="lead-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Этап *</Label>
              <Select
                value={formData.stage_id}
                onValueChange={(v) => setFormData({ ...formData, stage_id: v })}
              >
                <SelectTrigger data-testid="lead-stage-select">
                  <SelectValue placeholder="Выберите этап" />
                </SelectTrigger>
                <SelectContent>
                  {stages.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Сумма сделки</Label>
              <Input
                type="number"
                placeholder="0"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                data-testid="lead-amount-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Менеджер *</Label>
              <Select
                value={formData.manager_id}
                onValueChange={(v) => setFormData({ ...formData, manager_id: v })}
                disabled={!!user?.manager_id}
              >
                <SelectTrigger data-testid="lead-manager-select">
                  <SelectValue placeholder="Выберите менеджера" />
                </SelectTrigger>
                <SelectContent>
                  {managers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Дата визита</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !formData.visit_date && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.visit_date
                      ? format(formData.visit_date, 'PPP', { locale: ru })
                      : 'Выберите дату'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.visit_date}
                    onSelect={(d) => setFormData({ ...formData, visit_date: d })}
                    locale={ru}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Дата последнего платежа</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !formData.last_payment_date && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.last_payment_date
                      ? format(formData.last_payment_date, 'PPP', { locale: ru })
                      : 'Выберите дату'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.last_payment_date}
                    onSelect={(d) => setFormData({ ...formData, last_payment_date: d })}
                    locale={ru}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Комментарий</Label>
              <Textarea
                placeholder="Комментарий..."
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                data-testid="lead-comment-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleSave}
              disabled={!formData.name || !formData.manager_id || !formData.stage_id || saving}
              data-testid="confirm-save-lead"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Lead Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Информация о лиде</DialogTitle>
          </DialogHeader>

          {viewingLead && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Имя клиента</p>
                  <p className="font-medium">{viewingLead.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Этап</p>
                  <span
                    className={cn(
                      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1',
                      getStageStyle(viewingLead.stage_color)
                    )}
                  >
                    {viewingLead.stage_name}
                  </span>
                </div>
              </div>

              {/* Quick stage change buttons in view dialog */}
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Сменить этап</p>
                <div className="flex flex-wrap gap-2">
                  {stages.filter((s) => s.id !== viewingLead.stage_id).map((s) => (
                    <Button
                      key={s.id}
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() => {
                        handleQuickStageChange(viewingLead, s.id, viewingLead.manager_id);
                        setViewDialogOpen(false);
                      }}
                      data-testid={`view-lead-stage-${s.id}`}
                    >
                      → {s.name}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Сумма сделки</p>
                  <p className="font-medium">{viewingLead.amount ? formatCurrency(viewingLead.amount) : '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Менеджер</p>
                  <p className="font-medium">{viewingLead.manager_name_display || viewingLead.manager_name || '—'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Дата визита</p>
                  <p className="font-medium">
                    {viewingLead.visit_date
                      ? format(parseISO(viewingLead.visit_date), 'PPP', { locale: ru })
                      : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Дата последнего платежа</p>
                  <p className="font-medium">
                    {viewingLead.last_payment_date
                      ? format(parseISO(viewingLead.last_payment_date), 'PPP', { locale: ru })
                      : '—'}
                  </p>
                </div>
              </div>

              {viewingLead.comment && (
                <div>
                  <p className="text-sm text-muted-foreground">Комментарий</p>
                  <p className="mt-1">{viewingLead.comment}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Закрыть
            </Button>
            <Button
              onClick={() => {
                setViewDialogOpen(false);
                if (viewingLead) {
                  openEditDialog(viewingLead, viewingLead.manager_id);
                }
              }}
            >
              <Edit className="h-4 w-4 mr-2" />
              Редактировать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
